import type { Answers, CatalogSchema, Setup } from "../../services/catalogApi";
import { type Details, FOLLOW_UPS, type QuestionId, joinWords } from "./assessment";
import { blueprintPaper } from "./ui";

export interface Slot {
  id: string;
  label: string;
  value: string;
  filled: boolean;
  /** The question that changes this piece; none for the setup we work out. */
  question?: QuestionId;
  computed?: boolean;
}

const optionLabel = (schema: CatalogSchema, pick: string, id?: string) =>
  schema.picks.find((p) => p.id === pick)?.options.find((o) => o.id === id)?.label ?? "";

/** The eight pieces of a visitor's blueprint, filled in from their answers. */
export function blueprintSlots(schema: CatalogSchema, answers: Answers, details: Details, setup: Setup | null,
                               done: string[], followUps: QuestionId[]): Slot[] {
  const label = (key: keyof typeof FOLLOW_UPS, value: string) => FOLLOW_UPS[key].options.find(([v]) => v === value)?.[1];
  const shows = [
    details.freq && label("freq", details.freq),
    details.volume && `${label("volume", details.volume)} a month`,
    details.signoff.length ? `Signed off by ${joinWords(details.signoff.map((s) => label("signoff", s)?.toLowerCase() ?? s))}` : "",
    details.numbers.length ? `Checks ${joinWords(details.numbers.slice(0, 2)).toLowerCase()} first` : "",
  ].filter(Boolean) as string[];
  const problems = [...answers.problems, ...(details.problemText ? [`“${details.problemText.slice(0, 40)}”`] : [])];
  const business = [
    schema.business_models.find((b) => b.id === answers.business_model)?.label,
    ...answers.industries,
    answers.team_size ? `Team: ${answers.team_size}` : "",
  ].filter(Boolean) as string[];
  const slot = (id: string, text: string, value: string, question?: QuestionId, isDone = question ? done.includes(question) : false) =>
    ({ id, label: text, value, filled: isDone || !!value, question });
  return [
    slot("problem", "The problem", problems.join(", "), "problem"),
    slot("shows", "How it shows up", shows.join(" · "), followUps[0], followUps.some((q) => done.includes(q))),
    slot("workspace", "Where your team works", optionLabel(schema, "workspace", answers.picks.workspace), "workspace"),
    slot("data", "How private your data is", optionLabel(schema, "data", answers.picks.data), "data"),
    { id: "runs", label: "Where it will run", value: setup?.explanation ?? "", filled: !!setup, computed: true },
    slot("payment", "How you'd like to pay", optionLabel(schema, "payment", answers.picks.payment), "payment"),
    slot("tools", "Your everyday tools", joinWords(answers.tools), "tools"),
    slot("business", "Your business", business.join(" · "), "business", done.includes("business") || done.includes("team")),
  ];
}

function placeholder(slot: Slot, active: boolean) {
  if (slot.filled) return slot.value || "Skipped";
  if (active) return "Answering now";
  return slot.computed ? "Worked out from your answers" : "Fills in when you answer";
}

/** The blueprint: a column beside the questions, or a grid on the queue page. */
export default function Blueprint({ slots, active, layout, onChange, canChange }: {
  slots: Slot[];
  active?: string;
  layout: "column" | "grid";
  onChange: (question: QuestionId) => void;
  canChange: (slot: Slot) => boolean;
}) {
  const count = slots.filter((s) => !s.computed && s.filled).length;
  return (
    <div style={blueprintPaper} className="rounded-2xl border border-primary/20 p-5 sm:p-6">
      {layout === "column" && (
        <div className="mb-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-display text-lg font-bold text-text-primary">Your blueprint</h2>
            <span className="text-sm font-bold text-text-muted">{count} of 7 pieces in place</span>
          </div>
          <p className="text-sm text-text-muted">What we know so far. Tap Change on any piece.</p>
        </div>
      )}
      <ol className={layout === "grid" ? "grid gap-3 sm:grid-cols-2 lg:grid-cols-4" : "flex flex-col gap-2.5"}>
        {slots.map((slot) => {
          const isActive = active === slot.id;
          const tone = slot.computed && slot.filled
            ? "border-primary bg-primary text-white"
            : slot.filled ? "border-primary/30 bg-white" : isActive ? "border-dashed border-primary bg-white" : "border-dashed border-slate-300 bg-white/60";
          return (
            <li key={`${slot.id}-${slot.filled}`}
                className={`flex items-center gap-2 rounded-xl border-2 py-2.5 pl-4 pr-2 transition-colors ${slot.filled ? "catalog-pop" : ""} ${tone}`}>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className={`text-xs font-bold ${slot.computed && slot.filled ? "text-white/90" : slot.filled || isActive ? "text-primary" : "text-text-muted"}`}>
                  {slot.computed ? `${slot.label} · worked out for you` : slot.label}
                </span>
                <span className={`text-[15px] font-semibold ${slot.computed && slot.filled ? "text-white" : slot.filled ? "text-text-primary" : "text-text-muted"}`}>
                  {placeholder(slot, isActive)}
                </span>
              </div>
              {slot.question && canChange(slot) && !isActive && (
                <button type="button" onClick={() => onChange(slot.question!)}
                        aria-label={`Change ${slot.label.toLowerCase()}`}
                        className="min-h-[44px] flex-none rounded-lg px-3 text-sm font-bold text-primary hover:bg-primary/10">
                  Change
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
